import { applyGenericEntries, classifyGenericEntries } from '../../Inventory/helpers/genericEntries.helper';
import { fromExactSubdomainRegex } from '../../Inventory/helpers/subdomainRegex.helper';

import type { AppSitePublishersConfiguration } from '@/contexts/Targeting/TargetingContext';
import type { GenericImportPreview, GenericImportRow, InventoryOperation } from '../../AppAndSiteMarketplace.types';

type GenericImportColumns = {
  /** Index of the Include/Exclude column. */
  type: number;
  /** Index of the column with the domains/subdomains. */
  value: number;
};

const INCLUDE_VALUES = ['w', 'white', 'include', 'whitelisting'];
const EXCLUDE_VALUES = ['b', 'black', 'exclude', 'blacklisting'];

/** Same accepted values as the backend whitelisting/blacklisting import. */
export const normalizeImportType = (rawType: string | undefined): InventoryOperation | undefined => {
  const type = rawType?.trim().toLowerCase() ?? '';
  if (INCLUDE_VALUES.includes(type)) {
    return 'include';
  }
  return EXCLUDE_VALUES.includes(type) ? 'exclude' : undefined;
};

const OPERATIONS: InventoryOperation[] = ['include', 'exclude'];

/**
 * Classifies the CSV rows and computes what would be added to each list of the current configuration, without
 * applying anything. Rows without a value and without a type (blank lines) are ignored.
 */
export const buildGenericImportPreview = (
  csvRows: string[][],
  columns: GenericImportColumns,
  config: AppSitePublishersConfiguration
): GenericImportPreview => {
  const entries = csvRows
    .map((cells, index) => ({
      id: String(index),
      operation: normalizeImportType(cells[columns.type]),
      value: (cells[columns.value] ?? '').trim()
    }))
    .filter(({ operation, value }) => operation || value);

  const classified = {} as GenericImportPreview['classified'];
  const overLimit: Record<InventoryOperation, Set<string>> = { exclude: new Set(), include: new Set() };
  let skippedSubdomains = 0;

  OPERATIONS.forEach(operation => {
    const values = entries.filter(entry => entry.operation === operation).map(entry => entry.value);
    classified[operation] = classifyGenericEntries(values);
    const { patch, skippedSubdomains: skipped } = applyGenericEntries(config, operation, classified[operation]);
    const subdomainsKey = operation === 'include' ? 'whitelistedPublishers' : 'blacklistedPublishers';
    const stored = new Set(
      [...(config[subdomainsKey] ?? []), ...(patch[subdomainsKey] ?? [])].map(value =>
        (fromExactSubdomainRegex(value) ?? value).toLowerCase()
      )
    );
    for (const item of classified[operation].subdomains) {
      if (!stored.has(item.toLowerCase())) {
        overLimit[operation].add(item);
      }
    }
    skippedSubdomains += skipped;
  });

  const rows = entries.map<GenericImportRow>(({ id, operation, value }) => {
    if (!operation) {
      return { id, operation, status: 'invalid', value };
    }
    const { domains, subdomains } = classifyGenericEntries([value]);
    if (domains.length) {
      return { id, operation, status: 'domain', value };
    }
    if (subdomains.length) {
      return {
        id,
        operation,
        status: overLimit[operation].has(subdomains[0]) ? 'overLimit' : 'subdomain',
        value
      };
    }
    return { id, operation, status: 'invalid', value };
  });

  return {
    classified,
    invalidCount: rows.filter(row => row.status === 'invalid').length,
    rows,
    skippedSubdomains
  };
};

/** Configuration patch with the entries of the preview added to the include and exclude lists. */
export const applyGenericImportPreview = (
  config: AppSitePublishersConfiguration,
  preview: GenericImportPreview
): AppSitePublishersConfiguration => ({
  ...applyGenericEntries(config, 'include', preview.classified.include).patch,
  ...applyGenericEntries(config, 'exclude', preview.classified.exclude).patch
});
